import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { WorkspaceService } from '@app/feature/header/workspace-controls/workspace-services/workspace.service'
import { AccessSelectorComponent } from '@shared/access-selector/access-selector.component'
import { ColorPickerComponent, PickerColor } from '@shared/color-picker/color-picker.component'
import { EnvDirective } from '@shared/env.directive'
import { IconPickerComponent, PickerIcon } from '@shared/icon-picker/icon-picker.component'
import { SetType } from '@shared/models/picker'
import { ThemePalettes } from '@components-library/models'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { CommonDialogResultStatus } from '@components-library/tb-confirmation-popup/tb-confirmation-dialog.component'
import { TbInputComponent } from '@components-library/tb-input/tb-input.component'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { isNonNull, whitespaceValidationPattern } from '@core/global-util'
import {
    FieldEntities,
    InitResponseModel,
    isWorkspaceGlobal,
    Schema,
    SystemTypeCode,
    WorkspaceFieldNames,
    WorkspaceViewRecord,
} from '@core/models'
import { CreateWorkspaceData, Workspace } from '@models/ui'
import { dirtyCheck } from '@ngneat/dirty-check-forms'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { FieldValidationService } from '@services/field-validation.service'
import {
    FieldFacadeService,
    SchemaFacadeService,
    WorkspaceFacadeService,
} from '@services/store-facade'
import { filter, Observable, of, Subscription, switchMap } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { AdvancedOptionsComponent } from '@app/feature/header/workspace-controls/advanced-options/advanced-options.component'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { BreakpointService } from '@components-library/services/breakpoint.service'
import { schemaIconByName } from '@shared/models/icons'
import { toSignal } from '@angular/core/rxjs-interop'

export interface WorkspaceDialogData {
    updateData: WorkspaceDialogUpdateData
    workspace: Workspace
}

export interface WorkspaceDialogUpdateData {
    name: string
    color: string
    icon: string
}

export interface WorkspaceDialogControls {
    name: FormControl<string | null>
    icon: FormControl<string | null>
    color: FormControl<string | null>
}

@UntilDestroy()
@Component({
    selector: 'app-add-workspace',
    templateUrl: './add-workspace.component.html',
    imports: [
        TranslocoModule,
        ModalLayoutComponent,
        TbInputComponent,
        ReactiveFormsModule,
        ColorPickerComponent,
        IconPickerComponent,
        EnvDirective,
        AccessSelectorComponent,
        AdvancedOptionsComponent,
        TbButtonComponent,
        ModalLayoutButtonsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddWorkspaceComponent
    extends ModalContainerComponent<WorkspaceDialogData>
    implements OnInit
{
    private readonly workspaceService = inject(WorkspaceService)
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly translation = inject(TranslocoService)
    private readonly fieldValidationService = inject(FieldValidationService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly breakpointService = inject(BreakpointService)

    protected readonly schemaTabs = toSignal(
        this.schemaFacadeService.selectTableSchemas$.pipe(
            map((schemas) => {
                return schemas.map((schema) => {
                    return {
                        name: this.translation.translate(`nav_menu.${schema.name}`),
                        icon: schemaIconByName[schema.name],
                        id: schema.object_type_code || '',
                    }
                })
            }),
        ),
    )
    protected templateSchemas!: Schema[]
    protected templateViews!: WorkspaceViewRecord
    protected templateFields!: Record<string, FieldEntities>
    protected inactiveFields?: string[]

    protected readonly SetType = SetType

    isDirty!: boolean
    icon!: string

    workspaceGroupControl!: FormGroup<WorkspaceDialogControls>

    color?: string
    defaultColor?: string
    defaultIcon?: string

    isGlobal = false

    dirtySubscription!: Subscription

    get isTablet() {
        return this.breakpointService.isTablet
    }

    ngOnInit() {
        this.workspaceFacadeService.selectWorkspaceTemplate$
            .pipe(filter(isNonNull))
            .subscribe((template) => {
                this.templateSchemas = template.field_related_schemas
                this.templateFields = template.fields_by_schemas
                this.templateViews = template.views
            })

        this.fieldFacadeService
            .selectSchemaFieldBySystemName$(
                SystemTypeCode.WORKSPACE,
                WorkspaceFieldNames.WORKSPACE_NAME,
            )
            .pipe(untilDestroyed(this), filter(isNonNull))
            .subscribe((nameField) => {
                this.workspaceGroupControl = new FormGroup<WorkspaceDialogControls>({
                    name: new FormControl(this.data?.updateData.name ?? '', {
                        validators: [
                            Validators.required,
                            Validators.pattern(whitespaceValidationPattern),
                        ],
                        asyncValidators:
                            this.fieldValidationService.generateFieldValidators(nameField),
                    }),
                    icon: new FormControl(this.data?.updateData.icon ?? ''),
                    color: new FormControl(this.data?.updateData.color ?? ''),
                })
                const { icon, color } = this.data?.updateData ?? {}

                if (icon?.length) {
                    this.defaultIcon = icon
                }

                if (color?.length) {
                    this.defaultColor = color
                }

                if (this.data) {
                    this.isGlobal = isWorkspaceGlobal(this.data.workspace)
                }

                this.dirtyCheck()
            })
    }

    updateColor(picker: PickerColor) {
        this.workspaceGroupControl.controls.color.setValue(picker.color)
        this.color = picker.color

        if (picker.isDefault) this.dirtyCheck()
    }

    updateIcon(picker: PickerIcon) {
        if (picker.icon) {
            this.workspaceGroupControl.controls.icon.setValue(picker.icon)
            this.icon = picker.icon

            if (picker.isDefault) this.dirtyCheck()
        }
    }

    hasChanged(): boolean {
        return this.isDirty
    }

    apply() {
        if (this.workspaceGroupControl.invalid) {
            this.errors = [this.translation.translate('dialog_errors.incorrect_input')]
            return
        }

        if (!this.isDirty) {
            this.errors = [this.translation.translate('dialog_errors.no_changes_save')]
            return
        }

        const dialogData = {
            name: this.workspaceGroupControl.value.name ?? '',
            icon: this.icon ?? this.defaultIcon,
            color: this.color ?? this.defaultColor ?? '',
        }
        this.wrapResponse(
            this.data
                ? this.updateWorkspace(dialogData, this.data.workspace)
                : this.createWorkspaceWithTemplateFields(dialogData),
        )
    }

    delete() {
        if (this.data) {
            const deleteObservable = this.confirmationDialogService
                .openCommon({
                    translations: {
                        title: 'delete_workspace.title',
                        message: 'delete_workspace.message',
                        cancel: 'buttons.cancel',
                        confirm: 'buttons.delete',
                    },
                    colors: {
                        confirm: ThemePalettes.error,
                    },
                })
                .pipe(
                    filter((result) => result === CommonDialogResultStatus.CONFIRM),
                    switchMap(() => this.deleteWorkspace(this.data.workspace)),
                )

            this.wrapResponse(deleteObservable)
        }
    }

    private dirtyCheck() {
        this.dirtySubscription?.unsubscribe()

        this.dirtySubscription = dirtyCheck(
            this.workspaceGroupControl,
            of(this.workspaceGroupControl.value),
            {
                debounce: 0,
                useBeforeunloadEvent: false,
            },
        )
            .pipe(untilDestroyed(this))
            .subscribe((isDirty) => {
                this.isDirty = isDirty
            })
    }

    private createWorkspaceWithTemplateFields(data: WorkspaceDialogUpdateData) {
        return this.workspaceFacadeService.selectSelectedWorkspace$.pipe(
            take(1),
            switchMap((selectedWorkspace) => {
                return this.workspaceService.createWorkspace(
                    this.createModelData(selectedWorkspace, data),
                )
            }),
        )
    }

    private createModelData(
        selectedWorkspace: Workspace,
        data: WorkspaceDialogUpdateData,
    ): Observable<CreateWorkspaceData> {
        return this.workspaceService
            .getNewWorkspaceTemplate(
                this.templateViews,
                this.templateSchemas,
                this.templateFields,
                this.inactiveFields,
            )
            .pipe(
                map((templateData) => {
                    return {
                        configuration: this.combineModelConfigField(selectedWorkspace, data),
                        ...templateData,
                    }
                }),
            )
    }

    private updateWorkspace(data: WorkspaceDialogUpdateData, workspace: Workspace) {
        const model = {
            configuration: this.combineModelConfigField(workspace, data),
        }

        return this.workspaceService.updateWorkspace(workspace, model)
    }

    private combineModelConfigField(workspace: Workspace, data: WorkspaceDialogUpdateData) {
        return {
            name: {
                new_value: data.name,
                cell: workspace.name,
            },
            color: {
                new_value: data.color,
                cell: workspace.color,
            },
            icon: {
                new_value: data.icon,
                cell: workspace.icon,
            },
        }
    }

    private deleteWorkspace(workspace: Workspace) {
        return this.workspaceService.deleteWorkspace(workspace)
    }

    private wrapResponse(workspaceResponseObservable: Observable<InitResponseModel>) {
        this.wrapResponseObservable<InitResponseModel>(workspaceResponseObservable).subscribe(
            (data) => {
                if (data.status === 'success') {
                    this.close()
                }
            },
        )
    }
}
